<script setup lang="ts">
  import { computed } from 'vue';
  import { Icon, Typo } from '@/components';

  interface StepperEmits {
    (e: 'update:modelValue', data: number);
  }
  interface StepperProps {
    min?: number;
    max?: number;
    modelValue: number;
  }

  const emit = defineEmits<StepperEmits>();

  const props = withDefaults(defineProps<StepperProps>(), {
    min: 1,
    max: 29,
  });

  const isBoundary = (value: number) => props.modelValue === value;

  const minusDisabled = computed(() => isBoundary(props.min));
  const plusDisabled = computed(() => isBoundary(props.max));
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div class="tl-stepper">
    <button
      :disabled="minusDisabled"
      :class="['tl-stepper__button', { 'tl-stepper__button_disabled': minusDisabled }]"
      @click="emit('update:modelValue', modelValue - 1)"
    >
      <Icon name="dash" size="m" class="tl-stepper__icon" />
    </button>
    <Typo class="tl-stepper__typo" variant="body-l-500">{{ modelValue }}</Typo>
    <button
      :disabled="plusDisabled"
      :class="['tl-stepper__button', { 'tl-stepper__button_disabled': plusDisabled }]"
      @click="emit('update:modelValue', modelValue + 1)"
    >
      <Icon name="plus" size="m" class="tl-stepper__icon" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
  .tl-stepper {
    $root: &;

    align-items: center;
    display: flex;
    gap: var(--spacing-xs);

    &__typo {
      display: flex;
      justify-content: center;
      width: 20px;
    }

    &__icon {
      fill: var(--color-text-inverse);
    }

    &__button {
      background-color: var(--color-brand-secondary);
      border: none;
      border-radius: var(--rounding-s);
      cursor: pointer;
      height: 30px;
      padding: 0;
      width: 30px;

      &:hover {
        box-shadow: inset 0 0 0 100px rgb(0 0 0 / 0.08);
      }

      &:active {
        box-shadow: inset 0 0 0 100px rgb(0 0 0 / 0.16);
      }

      &_disabled {
        background-color: var(--color-disabled-100);
        #{$root}__icon {
          fill: var(--color-text-primary);
        }

        &:hover,
        &:active {
          box-shadow: none;
        }
      }
    }
  }
</style>
